import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "@/components/layout/Footer";
import WhatsAppButton from "@/components/ui/WhatsAppButton";
import ProductActionButton from "@/components/ui/ProductActionButton";
import ChromeStoreTestimonials from "@/components/ui/ChromeStoreTestimonials";
import { getProductBySlug } from "@/constants/products";
import { useChromeWebStore } from "@/hooks/useChromeWebStore";

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const product = slug ? getProductBySlug(slug) : undefined;
  const store = useChromeWebStore(product?.chromeWebStoreId);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.location.hash === "#privacy") {
      requestAnimationFrame(() => {
        document.getElementById("privacy")?.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, [slug]);

  if (!product) {
    return (
      <div className="min-h-screen bg-primary text-slate-light flex flex-col items-center justify-center gap-6 px-6">
        <h1
          className="uppercase text-center"
          style={{
            fontFamily: "'League Gothic', sans-serif",
            fontSize: "clamp(2.5rem, 8vw, 5rem)",
          }}
        >
          Product not found
        </h1>
        <button
          onClick={() => navigate("/")}
          className="text-xs uppercase tracking-widest text-white/60 hover:text-white"
        >
          ← Back home
        </button>
      </div>
    );
  }

  return (
    <div className="bg-primary text-slate-light min-h-screen antialiased overflow-x-hidden">
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-8 md:px-16 lg:px-24 py-6 bg-primary/80 backdrop-blur-sm">
        <button
          onClick={() => navigate("/")}
          className="text-base font-black uppercase tracking-[0.2em] text-white transition-opacity hover:opacity-60"
          style={{ fontFamily: "'League Gothic', sans-serif" }}
        >
          MS
        </button>
        <button
          onClick={() => navigate(-1)}
          className="text-xs uppercase tracking-widest text-white/50 hover:text-white transition-colors"
        >
          ← Back
        </button>
      </header>

      <main className="pt-28 pb-20 page-enter">
        {/* Hero */}
        <section className="px-6 md:px-12 lg:px-24 mb-16">
          <p className="text-white/35 text-xs uppercase tracking-[0.3em] mb-4 font-mono">
            {product.subtitle}
          </p>
          <h1
            className="uppercase text-[#e4e4e4] leading-none tracking-tight mb-6"
            style={{
              fontFamily: "'League Gothic', sans-serif",
              fontSize: "clamp(3rem, 10vw, 8rem)",
            }}
          >
            {product.title}
          </h1>
          <p className="text-[#888] text-sm md:text-base leading-relaxed max-w-2xl mb-8">
            {product.description}
          </p>
          <div className="flex flex-wrap gap-3">
            {product.links.map((link, index) => (
              <ProductActionButton
                key={link.label}
                label={link.label}
                href={link.href}
                external={link.external}
                variant={link.variant ?? (index === 0 ? "primary" : "secondary")}
              />
            ))}
          </div>
        </section>

        {/* Cover */}
        <section className="px-6 md:px-12 lg:px-24 mb-20">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#111] flex items-center justify-center min-h-[280px] max-h-[560px]">
            <img
              src={product.image}
              alt={product.title}
              className="w-full h-auto max-h-[560px] object-contain object-center p-4 md:p-6"
            />
          </div>
        </section>

        {/* Stats */}
        <section className="px-6 md:px-12 lg:px-24 mb-20">
          <h2
            className="uppercase text-[#e4e4e4] mb-8 leading-none"
            style={{
              fontFamily: "'League Gothic', sans-serif",
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
            }}
          >
            Current Reach
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {product.stats.map((stat) => {
              const isLiveUsers = stat.live === "chrome-web-store-users";
              const isLiveRating = stat.live === "chrome-web-store-rating";
              const liveValue = isLiveUsers
                ? store.installs
                : isLiveRating
                  ? store.rating
                  : null;
              const displayValue = liveValue || stat.value;
              const isLoading =
                (isLiveUsers || isLiveRating) && store.loading && !liveValue;

              return (
                <div
                  key={stat.label}
                  className="border border-white/10 px-5 py-6"
                >
                  <p className="text-white/30 text-[10px] uppercase tracking-[0.25em] mb-3">
                    {stat.label}
                  </p>
                  <p
                    className={`text-[#e4e4e4] text-lg md:text-xl font-medium flex items-center gap-2 ${
                      isLoading ? "opacity-40" : ""
                    }`}
                    aria-live={isLiveUsers || isLiveRating ? "polite" : undefined}
                  >
                    {displayValue}
                    {liveValue && (
                      <span
                        className="relative flex h-1.5 w-1.5"
                        title="Live from Chrome Web Store"
                        aria-hidden="true"
                      >
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      </span>
                    )}
                  </p>
                  {isLiveRating && store.reviewCount && (
                    <p className="mt-2 text-white/35 text-[10px] uppercase tracking-[0.2em]">
                      {store.reviewCount}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Tech */}
        <section className="px-6 md:px-12 lg:px-24 mb-20">
          <h2
            className="uppercase text-[#e4e4e4] mb-8 leading-none"
            style={{
              fontFamily: "'League Gothic', sans-serif",
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
            }}
          >
            Technologies
          </h2>
          <div className="flex flex-wrap gap-3">
            {product.technologies.map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 border border-white/15 text-[#aaa] text-xs uppercase tracking-widest"
              >
                {tech}
              </span>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="px-6 md:px-12 lg:px-24 mb-20">
          <h2
            className="uppercase text-[#e4e4e4] mb-8 leading-none"
            style={{
              fontFamily: "'League Gothic', sans-serif",
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
            }}
          >
            What It Does
          </h2>
          <ul className="space-y-4 max-w-2xl">
            {product.features.map((feature) => (
              <li
                key={feature}
                className="text-[#888] text-sm md:text-base leading-relaxed border-l border-white/15 pl-5"
              >
                {feature}
              </li>
            ))}
          </ul>
        </section>

        {/* Screenshots */}
        <section className="px-6 md:px-12 lg:px-24 mb-20">
          <h2
            className="uppercase text-[#e4e4e4] mb-8 leading-none"
            style={{
              fontFamily: "'League Gothic', sans-serif",
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
            }}
          >
            Screenshots
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {product.screenshots.map((shot, i) => (
              <div
                key={`${product.slug}-shot-${i}`}
                className="overflow-hidden rounded-xl border border-white/10 bg-[#111] flex items-center justify-center p-3"
              >
                <img
                  src={shot}
                  alt={`${product.title} screenshot ${i + 1}`}
                  className="w-full h-auto object-contain"
                />
              </div>
            ))}
          </div>
        </section>

        {product.chromeWebStoreId && (
          <ChromeStoreTestimonials
            reviews={store.reviews}
            loading={store.loading}
            storeUrl={`https://chromewebstore.google.com/detail/${product.chromeWebStoreId}`}
          />
        )}

        {product.privacy && (
          <section
            id="privacy"
            className="px-6 md:px-12 lg:px-24 mb-10 scroll-mt-28"
          >
            <h2
              className="uppercase text-[#e4e4e4] mb-8 leading-none"
              style={{
                fontFamily: "'League Gothic', sans-serif",
                fontSize: "clamp(2rem, 5vw, 3.5rem)",
              }}
            >
              Privacy Policy
            </h2>
            <div className="space-y-4 max-w-3xl">
              {product.privacy.map((paragraph) => (
                <p
                  key={paragraph}
                  className="text-[#888] text-sm md:text-base leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ProductDetailPage;
